import type { BandSelectionState } from './model'
import type { ListSchema } from './schema'

export interface ReconciledSelection<T> {
  readonly selectedIds: ReadonlySet<string>
  readonly selectedRecords: readonly T[]
}

export interface SelectionSummary {
  readonly filteredSelectableIds: readonly string[]
  readonly allFilteredSelected: boolean
  readonly someFilteredSelected: boolean
}

export function reconcileSelection<T>(
  records: readonly T[],
  schema: ListSchema<T>,
  selectedIds: ReadonlySet<string>,
): ReconciledSelection<T> {
  const reconciledIds = new Set<string>()
  const selectedRecords: T[] = []

  for (const record of records) {
    const recordId = schema.getId(record)
    if (!selectedIds.has(recordId)) continue
    reconciledIds.add(recordId)
    selectedRecords.push(record)
  }

  return { selectedIds: reconciledIds, selectedRecords }
}

export function selectionSummary(
  filteredSelectableIds: readonly string[],
  selectedIds: ReadonlySet<string>,
): SelectionSummary {
  const selectedFilteredCount = filteredSelectableIds.filter(id =>
    selectedIds.has(id),
  ).length
  const allFilteredSelected =
    filteredSelectableIds.length > 0 &&
    selectedFilteredCount === filteredSelectableIds.length

  return {
    filteredSelectableIds,
    allFilteredSelected,
    someFilteredSelected: selectedFilteredCount > 0 && !allFilteredSelected,
  }
}

export function bandSelectionState(
  selectableIds: readonly string[],
  selectedIds: ReadonlySet<string>,
): BandSelectionState {
  if (selectableIds.length === 0) return 'none'

  const selectedCount = selectableIds.filter(id => selectedIds.has(id)).length
  if (selectedCount === 0) return 'none'
  if (selectedCount === selectableIds.length) return 'all'
  return 'partial'
}
